import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { creditNudgeAtom } from '@/atoms/creditNudge';
import { hasActiveDialogueAtom } from '@/atoms/dialogQueue';
import { isDialogStageOpenAtom } from '@/components/DialogStage/atom';
import { useHasRole } from '@/hooks/auth';
import { PageDialog } from '@/components/PageDialog/PageDialog';
import { hasBeenDismissed } from '@/components/PagePopup/utils/hasBeenDismissed';
import { isLive } from '@/components/PagePopup/utils/isLive';
import { shouldNotShowOnCurrentPage } from '@/components/PagePopup/utils/shouldNotShowOnCurrentPage';
import { shouldShowOnCurrentPage } from '@/components/PagePopup/utils/shouldShowOnCurrentPage';
import { log } from '@/lib/utils';
import { logEvent } from '@/pages/api/v1/utils';
import { getAdaptiveMusicIntroProps } from './dialogues/getAdaptiveMusicIntroProps';
import { getSafelistChannelPromptProps } from './dialogues/getSafelistChannelPromptProps';
import { SAFELIST_PROMPT_STORAGE_KEY, useSafelistChannelPromptEligibility } from './dialogues/SafelistChannelPrompt/useSafelistChannelPromptEligibility';
import type { PageDialogProps } from '@/components/PageDialog/PageDialog';

// Module-level so its identity is stable: PageDialog re-runs onShow whenever
// the callback changes.
const logSafelistPromptViewed = () => {
  void logEvent({
    event_type: 'safelist_prompt_viewed',
    asPath: window.location.pathname + window.location.search
  });
};
const getNextDialogInQueue = ({
  items,
  pathname
}: {
  items: PageDialogProps[];
  pathname: string;
}): number => {
  if (!items || items.length === 0) {
    return -1;
  }
  return items.findIndex(item => isLive(item.duration) && shouldShowOnCurrentPage({
    pathname,
    popupIncludeSlugs: item.includeSlugs
  }) && !shouldNotShowOnCurrentPage({
    pathname,
    popupExcludeSlugs: item.excludeSlugs
  }) && !hasBeenDismissed({
    storageKey: item.storageKey,
    storageType: item.storageType,
    forceShow: item.forceShow
  }) && !item.isHidden);
};
const PageDialogQueue = () => {
  const [items, setItems] = useState<PageDialogProps[]>([]);
  const setHasActiveDialogue = useSetAtom(hasActiveDialogueAtom);
  const creditNudge = useAtomValue(creditNudgeAtom);
  const isDialogStageOpen = useAtomValue(isDialogStageOpenAtom);
  const isFree = useHasRole('Free');
  const isCreditNudgePending = creditNudge === 'pending' && isFree;

  // Defer while a stage flow (download journey, auth…) is open — a dialogue
  // must not appear behind or on top of it (DEV-6707)
  const isDeferred = isCreditNudgePending || isDialogStageOpen;
  const router = useRouter();
  const {
    asPath
  } = router;
  const isAdaptiveMusicNewEnabled = useFeatureIsOn('adaptive-music-new');
  const isAdaptiveMusicEnabled = useFeatureIsOn('adaptive-music');
  const isSafelistPromptEligible = useSafelistChannelPromptEligibility();
  const safelistChannelPrompt = useMemo(() => {
    const props = getSafelistChannelPromptProps({
      isEligible: isSafelistPromptEligible
    });

    // Added here, not in the shared props: the admin modal preview reuses
    // those and must not log an impression.
    return props && {
      ...props,
      onShow: logSafelistPromptViewed
    };
  }, [isSafelistPromptEligible]);
  const adaptiveMusicIntro = useMemo(() => getAdaptiveMusicIntroProps({
    isEnabled: isAdaptiveMusicEnabled && isAdaptiveMusicNewEnabled
  }), [isAdaptiveMusicEnabled, isAdaptiveMusicNewEnabled]);
  const getItems = useCallback((): PageDialogProps[] => {
    const dialoguesToShow: (PageDialogProps | null)[] = [];

    // First in the queue: the safelist prompt takes precedence over every
    // other dialogue and popup. The only exception, the failed-payment popup,
    // is handled inside useSafelistChannelPromptEligibility — an overdue
    // payment makes the prompt ineligible so the popup queue surfaces it.
    if (safelistChannelPrompt) {
      dialoguesToShow.push(safelistChannelPrompt);
    }
    if (adaptiveMusicIntro) {
      dialoguesToShow.push(adaptiveMusicIntro);
    }
    return dialoguesToShow.filter((d): d is PageDialogProps => d !== null);
  }, [safelistChannelPrompt, adaptiveMusicIntro]);
  const debouncedGetItemsRef = useRef(debounce((getItemsFn: () => PageDialogProps[]) => {
    const dialogueItems = getItemsFn();
    setItems(dialogueItems);
  }, 1500));
  useEffect(() => {
    debouncedGetItemsRef.current(getItems);
    return () => {
      debouncedGetItemsRef.current.cancel();
    };
  }, [getItems]);
  const nextToShowIndex = useMemo(() => getNextDialogInQueue({
    items,
    pathname: asPath
  }), [items, asPath]);
  const currentItem = useMemo(() => {
    if (!items || items.length === 0) {
      return null;
    }
    if (nextToShowIndex < 0 || nextToShowIndex >= items.length) {
      return null;
    }
    return items[nextToShowIndex];
  }, [items, nextToShowIndex]);

  // Inform the popup queue whether a dialogue is currently active
  useEffect(() => {
    setHasActiveDialogue(!!currentItem && !isDeferred);
  }, [currentItem, isDeferred, setHasActiveDialogue]);

  // Dev-only debug logging for the safelist prompt (DEV-5907): the
  // eligibility hook logs its own gates; this covers the queue-level filters.
  useEffect(() => {
    if (!safelistChannelPrompt) {
      return;
    }
    const isInQueue = items.some(item => item.storageKey === SAFELIST_PROMPT_STORAGE_KEY);
    if (!isInQueue) {
      log('🧪 [SafelistPrompt] eligible but not in the dialogue queue yet (the queue refresh is debounced by 1.5s)');
      return;
    }
    if (currentItem?.storageKey === SAFELIST_PROMPT_STORAGE_KEY) {
      log('🧪 [SafelistPrompt] active dialogue — PageDialog renders it after its delay', `(delay: ${safelistChannelPrompt.delay}ms)`);
      return;
    }
    log('🧪 [SafelistPrompt] in the dialogue queue but NOT showing:', {
      pathname: asPath,
      isMusicPage: shouldShowOnCurrentPage({
        pathname: asPath,
        popupIncludeSlugs: safelistChannelPrompt.includeSlugs
      }),
      isExcludedPage: shouldNotShowOnCurrentPage({
        pathname: asPath,
        popupExcludeSlugs: safelistChannelPrompt.excludeSlugs
      }),
      dismissedThisSession: hasBeenDismissed({
        storageKey: SAFELIST_PROMPT_STORAGE_KEY,
        storageType: 'sessionStorage'
      }),
      shownInstead: currentItem?.storageKey ?? null
    });
  }, [safelistChannelPrompt, items, currentItem, asPath]);
  if (!currentItem || isDeferred) {
    return null;
  }
  const dialogueKey = currentItem.revalidateKey ? `${currentItem.storageKey}-${currentItem.revalidateKey}` : currentItem.storageKey;
  return <PageDialog key={dialogueKey} {...currentItem} data-sentry-element="PageDialog" data-sentry-component="PageDialogQueue" data-sentry-source-file="index.tsx" />;
};
export default PageDialogQueue;